import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { validateRut, formatRut } from "@/lib/rut";
import { Truck, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";

const ROLES = [
  { value: "rentacar_admin", label: "Rent-a-Car (Flota)" },
  { value: "workshop_admin", label: "Taller Mecánico" },
  { value: "client_user", label: "Cliente Corporativo" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "rentacar_admin",
    company_name: "",
    company_rut: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.company_rut && !validateRut(form.company_rut)) {
      setError("RUT de la empresa inválido");
      return;
    }
    setLoading(true);
    const payload = {
      ...form,
      company_rut: form.company_rut ? formatRut(form.company_rut) : null,
    };
    const res = await register(payload);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
      <div className="w-full max-w-2xl bg-white border border-gray-200 p-8 md:p-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-white flex items-center justify-center p-1">
            <img src="/vetacar-logo.png" alt="VETA CAR" className="w-full h-full object-contain" />
          </div>
          <span className="font-display text-2xl">VETA CAR</span>
        </div>

        <p className="veta-tag mb-2">Crear cuenta</p>
        <h1 className="font-display text-4xl tracking-tighter leading-none">
          Registra tu empresa.
        </h1>
        <p className="mt-3 text-sm text-gray-600">
          Crea una cuenta para tu Rent-a-Car, Taller o empresa Cliente.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="veta-tag block mb-2">Tu nombre</label>
              <input
                required
                value={form.name}
                onChange={update("name")}
                className="w-full h-touch px-4 border border-gray-300 focus:outline-none focus:border-black"
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="veta-tag block mb-2">Tipo de cuenta</label>
              <select
                value={form.role}
                onChange={update("role")}
                className="w-full h-touch px-4 border border-gray-300 focus:outline-none focus:border-black bg-white"
                data-testid="register-role"
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="veta-tag block mb-2">Nombre de la empresa</label>
              <input
                required
                value={form.company_name}
                onChange={update("company_name")}
                className="w-full h-touch px-4 border border-gray-300 focus:outline-none focus:border-black"
                data-testid="register-company-name"
              />
            </div>
            <div>
              <label className="veta-tag block mb-2">RUT empresa (opcional)</label>
              <input
                value={form.company_rut}
                onChange={update("company_rut")}
                placeholder="76.123.456-7"
                className="w-full h-touch px-4 border border-gray-300 focus:outline-none focus:border-black font-mono"
                data-testid="register-company-rut"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="veta-tag block mb-2">Correo</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                className="w-full h-touch px-4 border border-gray-300 focus:outline-none focus:border-black font-mono"
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="veta-tag block mb-2">Contraseña</label>
              <input
                required
                type="password"
                minLength={6}
                value={form.password}
                onChange={update("password")}
                className="w-full h-touch px-4 border border-gray-300 focus:outline-none focus:border-black font-mono"
                data-testid="register-password"
              />
            </div>
          </div>

          {error && (
            <div className="status-danger px-4 py-3 text-sm" data-testid="register-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full h-touch flex items-center justify-center gap-3 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 transition-colors active:scale-[0.98] disabled:opacity-60"
            data-testid="register-submit-button"
          >
            {loading ? "Creando..." : "Crear cuenta"} <ArrowRight size={16} strokeWidth={2.5} />
          </button>

          <p className="text-sm text-center text-gray-600 pt-2">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-blue-600 font-bold underline" data-testid="link-login">
              Inicia sesión
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
